'use client';

import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import React from 'react';

import { GlobeIcon } from '@/components/icons/globe';
import { useI18n } from '@/features/i18n/components/client/I18nContext';
import type { Locale } from '@/features/i18n/types';
import { useIsAuthenticated } from '@/features/users/components/client/UserContext';
import { updatePreferredLanguageAction } from '@/features/users/server/actions';

export function LanguageSwitcher() {
  const { locale: currentLocale, t } = useI18n();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const isAuthenticated = useIsAuthenticated();
  // Local error message is no longer needed; toast is shown after navigation
  const [errorMsg] = React.useState<string | null>(null);
  const [selected, setSelected] = React.useState<Locale>(currentLocale as Locale);

  // Sync selected when context locale changes (after navigation)
  React.useEffect(() => {
    setSelected(currentLocale as Locale);
  }, [currentLocale]);

  async function handleLanguageChange(nextLocale: 'en' | 'es') {
    // Optimistically update the selected state to avoid flicker
    setSelected(nextLocale as Locale);

    // Mirror proxy cookie behavior for instant SSR consistency
    document.cookie = `locale=${nextLocale}; path=/`;

    // Replace the locale segment in the current path and preserve query string
    const segments = pathname.split('/');
    // Expecting path in the form /{locale}/... because component is rendered under [locale]
    if (segments.length > 1) {
      segments[1] = nextLocale;
    }
    const newPath =
      segments.join('/') + (searchParams?.toString() ? `?${searchParams.toString()}` : '');
    // Navigate first so UI switches immediately
    router.replace(newPath, { scroll: false });

    // Only attempt to save preference to backend if user is authenticated
    // On public pages (e.g., login), we just update the cookie and navigate
    if (!isAuthenticated) {
      return;
    }

    // Fire-and-forget preference sync; stash toast for next page load if it fails
    updatePreferredLanguageAction(nextLocale, newPath)
      .then((res) => {
        if (!res.ok) {
          const message = t(res.messageKey);
          sessionStorage.setItem('toast.language', message);
          sessionStorage.setItem('toast.language.ts', String(Date.now()));
          window.dispatchEvent(
            new CustomEvent('app:toast', { detail: { message, key: 'toast.language' } }),
          );
        }
      })
      .catch(() => {
        const message = t('toast.language.connection');
        sessionStorage.setItem('toast.language', message);
        sessionStorage.setItem('toast.language.ts', String(Date.now()));
        window.dispatchEvent(
          new CustomEvent('app:toast', { detail: { message, key: 'toast.language' } }),
        );
      });
  }

  return (
    <div className="flex items-center gap-3">
      {errorMsg && (
        <div className="absolute -top-10 right-0 bg-error-600 text-white text-xs px-2 py-1 rounded shadow">
          {errorMsg}
        </div>
      )}

      {/* Globe Icon */}
      <GlobeIcon size={20} className="text-primary-500" />

      {/* Language Options */}
      <div className="flex items-center gap-2">
        {/* ES Option */}
        <button
          onClick={() => void handleLanguageChange('es')}
          className={`text-sm font-medium transition-colors cursor-pointer ${
            selected === 'es' ? 'text-primary-500' : 'text-neutral-500 hover:text-neutral-700'
          }`}
          aria-label="Switch to Spanish"
        >
          ES
        </button>

        {/* Separator */}
        <div className="h-4 w-px bg-neutral-300" />

        {/* EN Option */}
        <button
          onClick={() => void handleLanguageChange('en')}
          className={`text-sm font-medium transition-colors cursor-pointer ${
            selected === 'en' ? 'text-primary-500' : 'text-neutral-500 hover:text-neutral-700'
          }`}
          aria-label="Switch to English"
        >
          EN
        </button>
      </div>
    </div>
  );
}
